(() => {
  function initCard(card, interval) {
    const slides = [...card.querySelectorAll('[data-bs-slide]')];
    if (slides.length < 2) return;

    const dotsWrap = card.querySelector('[data-bs-dots]');
    const prevBtn = card.querySelector('[data-bs-prev]');
    const nextBtn = card.querySelector('[data-bs-next]');
    const progress = card.querySelector('[data-bs-progress]');
    const media = card.querySelector('[data-bs-media]');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let index = 0;
    let timer = null;
    let paused = false;
    let touchStartX = 0;

    if (dotsWrap) {
      dotsWrap.innerHTML = slides
        .map(
          (_, i) =>
            `<button type="button" class="bs-card__dot${i === 0 ? ' is-active' : ''}" data-bs-dot="${i}" aria-label="Image ${i + 1}"></button>`
        )
        .join('');
    }
    const dots = [...(dotsWrap?.querySelectorAll('[data-bs-dot]') || [])];

    const setSlide = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      restartProgress();
    };

    const restartProgress = () => {
      if (!progress) return;
      progress.style.transition = 'none';
      progress.style.width = '0%';
      if (reduce || paused) return;
      void progress.offsetWidth;
      progress.style.transition = `width ${interval}ms linear`;
      progress.style.width = '100%';
    };

    const clear = () => {
      window.clearInterval(timer);
      timer = null;
    };

    const start = () => {
      clear();
      if (reduce || paused) return;
      restartProgress();
      timer = window.setInterval(() => setSlide(index + 1), interval);
    };

    const stop = () => {
      paused = true;
      clear();
      if (progress) {
        progress.style.transition = 'none';
        progress.style.width = '0%';
      }
    };

    const resume = () => {
      paused = false;
      start();
    };

    prevBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSlide(index - 1);
      if (!paused && !reduce) start();
    });
    nextBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      setSlide(index + 1);
      if (!paused && !reduce) start();
    });
    dots.forEach((dot) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        setSlide(Number(dot.dataset.bsDot));
        if (!paused && !reduce) start();
      });
    });

    media?.addEventListener('mouseenter', stop);
    media?.addEventListener('mouseleave', resume);
    media?.addEventListener('focusin', stop);
    media?.addEventListener('focusout', (e) => {
      if (!media.contains(e.relatedTarget)) resume();
    });

    // Desktop/tablet only: swipe on image changes slides.
    // On mobile, horizontal gestures must scroll the Crowd Puller rail.
    const mobileRail = window.matchMedia('(max-width: 899px)');
    media?.addEventListener(
      'touchstart',
      (e) => {
        if (mobileRail.matches) return;
        touchStartX = e.changedTouches[0].clientX;
        stop();
      },
      { passive: true }
    );
    media?.addEventListener(
      'touchend',
      (e) => {
        if (mobileRail.matches) return;
        const dx = e.changedTouches[0].clientX - touchStartX;
        if (Math.abs(dx) > 36) setSlide(index + (dx < 0 ? 1 : -1));
        resume();
      },
      { passive: true }
    );

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) resume();
            else stop();
          });
        },
        { threshold: 0.35 }
      );
      io.observe(card);
    } else {
      start();
    }

    setSlide(0);
  }

  function initMobileRail(root) {
    const rail = root.querySelector('[data-bs-rail]');
    if (!rail) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mq = window.matchMedia('(max-width: 899px)');
    // Lite mode (motion.js sets html.mk-lite on low-end phones): no auto-advance, the rail is swipe-only.
    if (reduce || document.documentElement.classList.contains('mk-lite')) return;

    // Auto-advance one card every few seconds with the browser's own smooth scroll (composited, no per-frame
    // layout reads or writes). Sizes are measured only when the rail resizes.
    const stepMs = 3400;
    const resumeMs = 3200;
    let timer = null;
    let resumeTimer = null;
    let onScreen = false;
    let fingerDown = false;
    let direction = 1;
    let step = 0;
    let max = 0;

    const measure = () => {
      const first = rail.firstElementChild;
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      step = first ? first.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
      max = Math.max(0, rail.scrollWidth - rail.clientWidth);
    };

    const stopLoop = () => {
      window.clearInterval(timer);
      timer = null;
    };

    const advance = () => {
      if (!onScreen || fingerDown || document.hidden || !mq.matches) return;
      if (!step) measure();
      if (max <= 4) return;
      const left = rail.scrollLeft;
      if (direction > 0 && left >= max - 6) direction = -1;
      else if (direction < 0 && left <= 6) direction = 1;
      const next = Math.max(0, Math.min(max, left + step * direction));
      rail.scrollTo({ left: next, behavior: 'smooth' });
    };

    const startLoop = () => {
      stopLoop();
      if (!mq.matches || fingerDown || !onScreen) return;
      timer = window.setInterval(advance, stepMs);
    };

    const holdForUser = () => {
      window.clearTimeout(resumeTimer);
      stopLoop();
    };
    const scheduleResume = (delay = resumeMs) => {
      window.clearTimeout(resumeTimer);
      resumeTimer = window.setTimeout(startLoop, delay);
    };

    rail.addEventListener(
      'touchstart',
      () => {
        fingerDown = true;
        holdForUser();
      },
      { passive: true, capture: true }
    );
    const releaseFinger = () => {
      fingerDown = false;
      scheduleResume();
    };
    rail.addEventListener('touchend', releaseFinger, { passive: true, capture: true });
    rail.addEventListener('touchcancel', releaseFinger, { passive: true, capture: true });
    rail.addEventListener('pointerdown', holdForUser, { passive: true });
    rail.addEventListener(
      'wheel',
      (e) => {
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 2) {
          holdForUser();
          direction = e.deltaX > 0 ? 1 : -1;
          scheduleResume(2200);
        }
      },
      { passive: true }
    );

    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(rail);
    else window.addEventListener('resize', measure);

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            onScreen = entry.isIntersecting;
            if (!onScreen) holdForUser();
            else scheduleResume(1600);
          });
        },
        { threshold: 0.35 }
      );
      io.observe(root);
    } else {
      onScreen = true;
      startLoop();
    }

    document.addEventListener('visibilitychange', () => (document.hidden ? holdForUser() : onScreen && scheduleResume(1600)));
    mq.addEventListener('change', () => {
      holdForUser();
      if (mq.matches) scheduleResume(1600);
      else rail.scrollLeft = 0;
    });
  }

  function initBestSellers(root) {
    const interval = Math.max(2500, Number(root.dataset.interval) || 4000);
    root.querySelectorAll('[data-bs-card]').forEach((card) => initCard(card, interval));
    initMobileRail(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-best-sellers]').forEach(initBestSellers);
  });
})();
